import {
  useDialogListener as useDialogListenerBase,
  useDialogTrigger as useDialogTriggerBase,
  emitCustomEvent as emitCustomEventBase
} from '@satellite/composables/useEventBasedDialog';

import type { BaseTaskCreateData, CheckTaskFormData, MoveTaskFormData } from '@nova/core';

export type OpenRemoveCarrierContactDialogEvent = {
  carrierContact: {
    id: string;
    isFavorite: boolean;
    emailCCs: string[];
    company?: {
      name: string;
    };
    user: {
      company?: {
        name: string;
      };
      firstName?: string;
      lastName?: string;
      fullName?: string;
    };
  };
  mixpanelData?: unknown;
  onContactRemoved?: () => void;
};

export type OpenCreateCarrierCompanyDialogEvent = {
  mixpanelEntryPoint: string;
  onCompanyCreated?: (company: unknown) => void;
};

export type OpenCreateCarrierContactDialogEvent = {
  mixpanelEntryPoint: string;
  onContactCreated?: (contact: unknown) => void;
};

export type OpenCreateYardTaskDialogEvent = BaseTaskCreateData & {
  warehouseId?: string;
  warehouseTimezone?: string;
  onTaskCreated?: (task: unknown) => void;
};

export type OpenCreateYardMoveTaskDialogEvent = OpenCreateYardTaskDialogEvent &
  MoveTaskFormData & {
    onSkipCreateTask?: () => void;
  };

export type OpenCreateYardCheckTaskDialogEvent = OpenCreateYardTaskDialogEvent & CheckTaskFormData;

export type OpenEditYardTaskDialogEvent = {
  taskId: string;
  warehouseTimezone: string;
  onTaskUpdated?: (task: unknown) => void;
};

export type OpenGateFeedImageCarouselEvent = {
  images: string[];
};

export type OpenGateFeedAddAssetDialogEvent = {
  vehicleObservation: unknown;
};

export type OpenLimitsOverviewDrawerEvent = {
  dateMode: 'day' | 'week' | 'month';
  baseDate: string; // ISO date string
  warehouseId: string;
  timezone: string;
  isMilitaryTimeEnabled: boolean;
};

export type CustomEventMap = {
  /**
   * list here all the custom events that are used in the app
   * so the IDE's type inference helps us when emitting the events.
   */
  'open-remove-carrier-contact-dialog': CustomEvent<OpenRemoveCarrierContactDialogEvent>;
  'open-create-carrier-company-dialog': CustomEvent<OpenCreateCarrierCompanyDialogEvent>;
  'open-create-carrier-contact-dialog': CustomEvent<OpenCreateCarrierContactDialogEvent>;
  'open-create-yard-task-drawer': CustomEvent<
    OpenCreateYardMoveTaskDialogEvent | OpenCreateYardCheckTaskDialogEvent
  >;
  'open-create-yard-move-task-drawer': CustomEvent<OpenCreateYardMoveTaskDialogEvent>;
  'open-gate-feed-image-carousel': CustomEvent<OpenGateFeedImageCarouselEvent>;
  'open-gate-feed-add-asset-dialog': CustomEvent<OpenGateFeedAddAssetDialogEvent>;
  'open-edit-yard-task-drawer': CustomEvent<OpenEditYardTaskDialogEvent>;
  'open-limits-overview-drawer': CustomEvent<OpenLimitsOverviewDrawerEvent>;
};

/**
 * Composable for listening to dialog events (used in dialog components)
 */
export function useDialogListener<K extends keyof CustomEventMap>(dialogName: K) {
  return useDialogListenerBase<CustomEventMap, K>(dialogName);
}

/**
 * Composable for triggering dialog events (used in components that open dialogs)
 */
export function useDialogTrigger<K extends keyof CustomEventMap>(dialogName: K) {
  return useDialogTriggerBase<CustomEventMap, K>(dialogName);
}

/**
 * Emit a custom event (overload without data)
 */
export function emitCustomEvent<K extends keyof CustomEventMap>(
  eventName: K,
  target: EventTarget
): void;

/**
 * Emit a custom event (overload with data)
 */
export function emitCustomEvent<K extends keyof CustomEventMap>(
  eventName: K,
  data: CustomEventMap[K]['detail'],
  target?: EventTarget
): void;

/**
 * Emit a custom event
 */
export function emitCustomEvent<K extends keyof CustomEventMap>(
  eventName: K,
  dataOrTarget?: CustomEventMap[K]['detail'] | EventTarget,
  maybeTarget?: EventTarget
): void {
  emitCustomEventBase<CustomEventMap, K>(eventName, dataOrTarget, maybeTarget);
}

declare global {
  interface DocumentEventMap extends CustomEventMap {}
}
